import type { LocationSchema } from '@libs/data-access/entities'
import type {
  AccountSchema,
  ScheduleMembershipSchema,
  ShiftRQSchema
} from '@libs/data-access/queries'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import { formatToIsoDay } from '@spa/utils/date'
import moment from 'moment'

import type FormData from '../Models/FormData'
import { canSetAutomaticBreaks } from '../utils/canSetAutomaticBreaks'
import getBreaks from '../utils/getBreaks'
import getMeals from '../utils/getMeals'

import useGetDefaultUserContract from './useGetDefaultUserContract'

type Props = {
  account: AccountSchema
  membership?: ScheduleMembershipSchema
  shift: ShiftRQSchema
  teamId: number
  labelId?: number
  weeklyScheduleIsPublished: boolean
  weeklyscheduleLocation: LocationSchema
}

const useShiftDefaultValues = ({
  account,
  membership,
  shift,
  weeklyscheduleLocation,
  teamId,
  labelId,
  weeklyScheduleIsPublished
}: Props): FormData => {
  const { tDates } = usePlanningFormTranslation()

  const momentStart = moment(shift.starts_at)
  const momentEnd = moment(shift.ends_at)
  const shiftStartDate = moment(shift.starts_at).format('YYYY-MM-DD')

  const automaticBreaksEnabled = canSetAutomaticBreaks(weeklyscheduleLocation, [
    momentStart.format('HH:mm'),
    momentEnd.format('HH:mm')
  ])
  const defaultBreakDuration = automaticBreaksEnabled
    ? Number(weeklyscheduleLocation.default_break_duration)
    : 0

  const defaultUser = useGetDefaultUserContract({ membership, shift })

  return {
    label: { value: labelId || shift.label_id || '' },
    user_contract: shift.user_contract_id ? [defaultUser] : [],
    shift_time_range: shift.id
      ? [momentStart.format('HH:mm'), momentEnd.format('HH:mm')]
      : ['__:__', '__:__'],
    breaks: getBreaks(defaultBreakDuration, shift?.shift_breaks, shift?.id),
    meals: getMeals(
      account.meal_by_default_for_real,
      weeklyscheduleLocation.default_meal_type,
      shift?.shift_meals,
      shift?.id
    ),
    note: shift.note || '',
    notify_user: weeklyScheduleIsPublished,
    team: { value: teamId },
    location: {
      value: weeklyscheduleLocation && weeklyscheduleLocation.id
    },
    update_day: {
      value: formatToIsoDay(shift.starts_at),
      label: tDates('dates.literalDate', { date: shift.starts_at })
    },
    days: [
      {
        value: shiftStartDate,
        label: tDates('dates.literalDate', { date: shiftStartDate })
      }
    ]
  }
}

export default useShiftDefaultValues
